import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { useAsyncData } from '~/hooks/useAsyncData'
import {
  ANCHOR_COPY,
  ROUTINE_ANCHOR_CADENCE,
  ROUTINE_ANCHOR_KINDS,
  ROUTINE_ANCHOR_LABELS,
  type RoutineAnchorKind,
  type RoutineAnchorRecord,
} from '~/models/routineAnchor'
import { saveAnchor } from '~/services/routineAnchorService'
import { useDatabase } from '~/storage/DatabaseContext'
import { listRoutineAnchors } from '~/storage/repositories/routineAnchorRepository'
import { STORE } from '~/storage/schema'
import { unwrapTime } from '~/types/dates'
import styles from './RoutineAnchorEditor.module.css'

/**
 * Setting routine times (D-099).
 *
 * **This is a preference screen, not a plan screen.** An anchor is never
 * evidence, no rule reads one, and a missed anchor writes nothing — so it lives
 * in Settings beside the other display choices rather than in My Plan, which
 * owns commitments and their history. My Plan shows the times read-only, because
 * seeing when you intended to do something is useful next to what you intended
 * to do.
 *
 * **The copy states what the app can actually do.** Not "we will remind you at
 * 07:30" — there is nowhere to push from until D-095, and a promise the app
 * cannot keep is worse than no feature.
 */

const CADENCE_LABELS: Readonly<Record<'daily' | 'weekly' | 'fortnightly', string>> = {
  daily: 'Every day',
  weekly: 'Once a week',
  fortnightly: 'Every couple of weeks',
}

export function RoutineAnchorEditor() {
  const db = useDatabase()
  const { state, reload } = useAsyncData(async () => {
    const anchors = await listRoutineAnchors(db)
    return anchors.ok ? anchors.value : []
  }, [STORE.routineAnchors])

  if (state.status !== 'ready') {
    return <p class={styles.body}>{state.status === 'loading' ? 'Loading…' : 'Unavailable.'}</p>
  }

  const byKind = new Map(state.value.map((anchor) => [anchor.kind, anchor]))

  return (
    <div class={styles.editor}>
      <h3 class={styles.heading}>{ANCHOR_COPY.editorHeading}</h3>
      <p class={styles.body}>{ANCHOR_COPY.editorLede}</p>
      <p class={styles.body}>{ANCHOR_COPY.cannotNotify}</p>

      <ul class={styles.list}>
        {ROUTINE_ANCHOR_KINDS.map((kind) => (
          <li key={kind} class={styles.row}>
            <AnchorRow kind={kind} anchor={byKind.get(kind)} onSaved={reload} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function AnchorRow({
  kind,
  anchor,
  onSaved,
}: {
  kind: RoutineAnchorKind
  anchor: RoutineAnchorRecord | undefined
  onSaved: () => void
}) {
  const db = useDatabase()
  const [time, setTime] = useState(
    anchor?.startTime === undefined ? '' : unwrapTime(anchor.startTime),
  )
  const [error, setError] = useState<string | undefined>(undefined)
  const label = ROUTINE_ANCHOR_LABELS[kind]

  const save = async (next: string): Promise<void> => {
    setError(undefined)
    /* An empty time is the explicit "no time set" state, and it also switches the
       anchor off — an enabled anchor with no time can never prompt, so leaving it
       on would be a setting that does nothing. */
    const saved = await saveAnchor(
      db,
      {
        kind,
        enabled: next === '' ? 'no' : 'yes',
        ...(next === '' ? {} : { startTime: next }),
      },
      new Date(),
    )

    if (!saved.ok) {
      setError(saved.error.message)
      return
    }
    onSaved()
  }

  /* The cadence is *described by* the field, not part of its name. Nesting it in
     the `<label>` made the accessible name "Weigh-inEvery day", which is both a
     worse thing to hear announced and — because Playwright matches a label by
     substring — ambiguous against the "Save Weigh-in time" button beside it.
     Same substring hazard as the heading collisions, one layer down. */
  const cadenceId = `anchor-${kind}-cadence`
  const errorId = `anchor-${kind}-error`
  const describedBy = [cadenceId, error === undefined ? undefined : errorId]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <label class={styles.label} for={`anchor-${kind}`}>
        {label}
      </label>
      <p class={styles.cadence} id={cadenceId}>
        {CADENCE_LABELS[ROUTINE_ANCHOR_CADENCE[kind]]}
      </p>

      <div class={styles.controls}>
        <input
          id={`anchor-${kind}`}
          class={styles.input}
          type="time"
          value={time}
          aria-describedby={describedBy}
          onInput={(event) => setTime((event.currentTarget as HTMLInputElement).value)}
        />
        <Button variant="secondary" onClick={() => void save(time)} label={`Save ${label} time`}>
          Save
        </Button>
        {anchor?.startTime === undefined ? null : (
          <Button
            variant="secondary"
            onClick={() => {
              setTime('')
              void save('')
            }}
            label={`Clear ${label} time`}
          >
            Clear
          </Button>
        )}
      </div>

      <p class={styles.state}>
        {anchor?.startTime === undefined || anchor.enabled !== 'yes'
          ? ANCHOR_COPY.noTimeSet
          : `Shown from ${unwrapTime(anchor.startTime)}`}
      </p>

      {error === undefined ? null : (
        <p class={styles.error} id={errorId} role="alert">
          {error}
        </p>
      )}
    </>
  )
}
